import styled from 'styled-components';

import { ClienteVendaPagamentoSecao } from '../../../../../../../types/cliente-venda';
import { CollapsibleSection } from '../../produtos/sections/collapsible-section';
import { formatDateTime } from '../format';
import { Row } from './styles';

interface Props {
  data: ClienteVendaPagamentoSecao;
  metodoPagamentoDescricao: string | null;
}

const Block = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 10px 12px;

  & + & {
    margin-top: 8px;
  }
`;

const BlockTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Mono = styled.code`
  display: block;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  background: ${({ theme }) => theme.colors.bgMuted};
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  word-break: break-all;
  white-space: pre-wrap;
`;

const Empty = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const PagamentoSection = ({ data, metodoPagamentoDescricao }: Props) => {
  const hasPix = data.pix && (data.pix.qrCode || data.pix.copiaCola);
  const hasBoleto =
    data.boleto &&
    (data.boleto.linhaDigitavel ||
      data.boleto.codigoBarras ||
      data.boleto.linkExterno);
  const hasPayPal = data.paypal && data.paypal.approveUrl;
  const hasStripe = data.stripe && data.stripe.clientSecret;
  const hasMercadoPago =
    data.mercadoPago && (data.mercadoPago.customerId || data.mercadoPago.cardId);
  const hasCartao = data.cartao && data.cartao.cvvCardId;
  const hasPaymentDetails =
    data.paymentDetails &&
    (data.paymentDetails.authorizationCode ||
      data.paymentDetails.last4 ||
      data.paymentDetails.providerTransactionId ||
      data.paymentDetails.acquirerReferenceNumber ||
      data.paymentDetails.providerTransactionAt ||
      data.paymentDetails.merchantDescriptor ||
      data.paymentDetails.capturedAt);

  const hasAny =
    hasPix ||
    hasBoleto ||
    hasPayPal ||
    hasStripe ||
    hasMercadoPago ||
    hasCartao ||
    hasPaymentDetails;

  return (
    <CollapsibleSection
      title={`Pagamento${metodoPagamentoDescricao ? ` · ${metodoPagamentoDescricao}` : ''}`}
      testId="venda-section-pagamento"
    >
      {!hasAny ? (
        <Empty>Sem dados específicos do método de pagamento.</Empty>
      ) : (
        <>
          {hasPix && (
            <Block>
              <BlockTitle>PIX</BlockTitle>
              {data.pix?.copiaCola && (
                <>
                  <Row>
                    <span>Copia & cola</span>
                    <span />
                  </Row>
                  <Mono>{data.pix.copiaCola}</Mono>
                </>
              )}
              {data.pix?.qrCode && (
                <Row>
                  <span>QR Code</span>
                  <span>Disponível ({data.pix.qrCode.length} chars)</span>
                </Row>
              )}
            </Block>
          )}

          {hasBoleto && (
            <Block>
              <BlockTitle>Boleto</BlockTitle>
              {data.boleto?.linhaDigitavel && (
                <>
                  <Row>
                    <span>Linha digitável</span>
                    <span />
                  </Row>
                  <Mono>{data.boleto.linhaDigitavel}</Mono>
                </>
              )}
              {data.boleto?.codigoBarras && (
                <>
                  <Row>
                    <span>Código de barras</span>
                    <span />
                  </Row>
                  <Mono>{data.boleto.codigoBarras}</Mono>
                </>
              )}
              {data.boleto?.linkExterno && (
                <Row>
                  <span>Link externo</span>
                  <span>
                    <a
                      href={data.boleto.linkExterno}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir boleto
                    </a>
                  </span>
                </Row>
              )}
            </Block>
          )}

          {hasPayPal && (
            <Block>
              <BlockTitle>PayPal</BlockTitle>
              <Row>
                <span>Approve URL</span>
                <span>
                  <a
                    href={data.paypal!.approveUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir aprovação
                  </a>
                </span>
              </Row>
            </Block>
          )}

          {hasStripe && (
            <Block>
              <BlockTitle>Stripe</BlockTitle>
              <Row>
                <span>Client secret</span>
                <span>{data.stripe!.clientSecret}</span>
              </Row>
            </Block>
          )}

          {hasMercadoPago && (
            <Block>
              <BlockTitle>Gateway</BlockTitle>
              {data.mercadoPago?.customerId && (
                <Row>
                  <span>Customer ID</span>
                  <span>{data.mercadoPago.customerId}</span>
                </Row>
              )}
              {data.mercadoPago?.cardId && (
                <Row>
                  <span>Card ID</span>
                  <span>{data.mercadoPago.cardId}</span>
                </Row>
              )}
            </Block>
          )}

          {hasCartao && (
            <Block>
              <BlockTitle>Cartão</BlockTitle>
              <Row>
                <span>CVV card ID</span>
                <span>{data.cartao!.cvvCardId}</span>
              </Row>
            </Block>
          )}

          {hasPaymentDetails && (
            <Block>
              <BlockTitle>Payment details (gateway)</BlockTitle>
              {data.paymentDetails?.authorizationCode && (
                <Row>
                  <span>Authorization code</span>
                  <span>{data.paymentDetails.authorizationCode}</span>
                </Row>
              )}
              {data.paymentDetails?.last4 && (
                <Row>
                  <span>Final do cartão</span>
                  <span>**** {data.paymentDetails.last4}</span>
                </Row>
              )}
              {data.paymentDetails?.providerTransactionId && (
                <Row>
                  <span>Provider transaction ID</span>
                  <span>{data.paymentDetails.providerTransactionId}</span>
                </Row>
              )}
              {data.paymentDetails?.acquirerReferenceNumber && (
                <Row>
                  <span>ARN</span>
                  <span>{data.paymentDetails.acquirerReferenceNumber}</span>
                </Row>
              )}
              {data.paymentDetails?.merchantDescriptor && (
                <Row>
                  <span>Merchant descriptor</span>
                  <span>{data.paymentDetails.merchantDescriptor}</span>
                </Row>
              )}
              {data.paymentDetails?.providerTransactionAt && (
                <Row>
                  <span>Transaction at</span>
                  <span>
                    {formatDateTime(data.paymentDetails.providerTransactionAt)}
                  </span>
                </Row>
              )}
              {data.paymentDetails?.capturedAt && (
                <Row>
                  <span>Captured at</span>
                  <span>{formatDateTime(data.paymentDetails.capturedAt)}</span>
                </Row>
              )}
            </Block>
          )}
        </>
      )}
    </CollapsibleSection>
  );
};
